import { Component, createRef } from 'react';

import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { PageDataProviderService } from '../../../services/page-data-provider.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { WebSocketService } from '../../../services/websocket.service';
import { FileUploadUtils } from '../../../utils/file-upoad.utils';
import { merge } from '../../../utils/lodash-min';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { AdvancedOptionsSectionComponent } from '../advanced-options-section/advanced-options-section.component';
import { ConvertOptionsContextV2, ConvertOptionsSummary } from '../../convert-options-v2/convert-options.context';
import { FileProcessEditorComponent } from '../../converter/file-process-editor.component';
import { UpdateProcessProbedDataFnProvider } from '../file-card/file-card.component';
import Modal from '../modal/modal.component';
import * as styles from './file-upload-reedit-modal.component.module.scss';

const RE_EDITED_FILES = 'RE_EDITED_FILES';


interface FileUploadReEditModalComponentProps {
    fileUpload: FileUpload;
}

interface ExtraProps {
    convertOptionsContext: ConvertOptionsContextV2;
    uploadInProgressFiles: UploadInProgressFile<File>[];
    onOptionChange: (data: ConvertOptionsSummary) => void;
}

export class FileUploadReEditModalComponent extends Component<FileUploadReEditModalComponentProps> implements UpdateProcessProbedDataFnProvider {
    private readonly logger = Logger.getLogger('FileUploadReEditModalComponent');

    modalRef = createRef<Modal>();
    editorRef = createRef<FileProcessEditorComponent>();
    uploadInProgressFiles: UploadInProgressFile<File>[] = [];
    options: any;
    extraProps: ExtraProps;

    @lazyInject(FileProcessService) fileProcessService: FileProcessService;
    @lazyInject(FileUploadService) fileUploadService: FileUploadService;
    @lazyInject(WebSocketService) websocketService: WebSocketService;
    @lazyInject(UIContext) uiContext: UIContext;
    @lazyInject(FileManagerService) fileManagerService: FileManagerService;
    @lazyInject(PageDataProviderService) optionsService: PageDataProviderService;
    @lazyInject(SpinnerService) spinnerService: SpinnerService;
    @lazyInject(ScriptLoadService) scriptLoadService: ScriptLoadService;

    constructor(props) {
        super(props);
    }

    async componentDidMount() {
        const instance = await this.spinnerService.show('Loading....');
        this.uploadInProgressFiles = await FileUploadUtils.toUploadInProgressFile(this.props.fileUpload);
        const options = this.props.fileUpload.optionsSnapshot;
        this.optionsService.loadConvertOptions(this.props.fileUpload.uniqueProcessType).subscribe({
            next: (functionalOptions) => {
                this.options = merge(functionalOptions, options);

                const ctx = ConvertOptionsContextV2.newContext(this.options);
                if (this.props.fileUpload.options) {
                    ctx.applySummary(this.props.fileUpload.options);
                }
                ctx.modelChangeObserver.pipe(untilDestroyed(this)).subscribe({
                    next: (summary: ConvertOptionsSummary) => {
                        this.convertOptionCategoriesChanged(summary);
                    }
                });

                this.extraProps = {
                    convertOptionsContext: ctx,
                    uploadInProgressFiles: this.uploadInProgressFiles,
                    onOptionChange: (data) => {
                        this.logger.debug({ message: `Data from ConvertOptionsComponent `, data });
                    },
                };
                this.logger.debug({ message: "Upload in-progress files", data: this.uploadInProgressFiles });
                this.spinnerService.hide(instance);
                this.forceUpdate();
            },
            error: (err) => {
                this.spinnerService.hide(instance);
                this.uiContext.handleError(err);
            }
        })

        // TODO if this failed uniqueProcessType options will be broken for all future converts until a page refresh
        // this.optionsService.loadConvertOptions(this.props).subscribe({
        //     next: (functionalOptions) => {
        //         this.options = merge(functionalOptions, options);
        //     }
        // });
        // TODO if this failed uniqueProcessType options will be broken for all future converts until a page refresh

        // this.optionsService.getOptionsFor(this.fileUpload.uniqueProcessType);

    }

    // removeFile, orientationChanged, convertOptionCategoriesChanged removed (handled by Editor or Options component)

    convertOptionCategoriesChanged(summary: ConvertOptionsSummary) {
        this.forceUpdate();
    }

    private converting = false;
    public async convert() {
        if (this.converting) return;
        this.converting = true;
        try {
            if (this.editorRef.current) {
                await this.editorRef.current.startConversion();
            }
        } finally {
            this.converting = false;
        }
    }



    hide() {
        this.modalRef && this.modalRef.current && this.modalRef.current.unmount();
    }

    updateProcessProbedDataFn() {
        // Delegated
        return (async () => { }) as any;
    }


    render() {
        return <Modal ref={this.modalRef} modalClass={styles['modal']}>
            <div data-modalheader>
                <h4 className="modal-title">Edit Conversion Settings</h4>
            </div>
            <div className="modal-body contact-us-modal" data-modalbody>
                {
                    this.uploadInProgressFiles && this.extraProps && <div className="mt-3 mb-3">
                        <FileProcessEditorComponent
                            ref={this.editorRef}
                            initialFiles={this.uploadInProgressFiles}
                            convertOptionsContext={this.extraProps.convertOptionsContext}
                            toType={this.props.fileUpload.toType}
                            fromType={this.props.fileUpload.fromType}
                            processType={this.props.fileUpload.processType}
                            pageData={this.props.fileUpload.pageData}
                            onConversionSuccess={() => this.hide()}
                            hideRemoveButton={true}
                        />
                    </div>
                }
                {
                    this.extraProps && <AdvancedOptionsSectionComponent
                        convertOptionsContext={this.extraProps.convertOptionsContext}
                        uploadInProgressFiles={this.extraProps.uploadInProgressFiles}
                        onOptionChange={this.extraProps.onOptionChange}
                        path={this.props.fileUpload.uniqueProcessType}
                    />
                }
            </div>
            <div data-modalfooter>
                <button className={`${styles['btn']} ${styles['btn-primary']}`} onClick={() => this.convert()}>Apply &amp; Restart</button>
                <button className={styles['btn']} onClick={() => this.hide()}>Cancel</button>
            </div>
        </Modal>
    }
}

